import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Award, Users, Globe, TrendingUp } from 'lucide-react';

export const About = () => {
  const stats = [
    { icon: Award, label: 'Kantonal bewilligt', value: 'Zertifiziert' },
    { icon: Users, label: 'Zufriedene Kunden', value: '500+' },
    { icon: Globe, label: 'Umweltbewusst', value: '100%' },
    { icon: TrendingUp, label: 'Jahre Erfahrung', value: '10+' }
  ];

  return (
    <section id="about" className="py-24 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <Badge className="bg-secondary/10 text-secondary border-secondary/30">
              Über Pneu-Idris
            </Badge>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Unsere Vision: Jeder Reifen birgt eine Geschichte
            </h2>
            
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p className="text-lg">
                <strong className="text-foreground">Pneu Idris</strong> entstand aus einer einfachen Vision: Jeder Reifen, ob neu oder gebraucht, birgt eine Geschichte und grosses Potenzial. Aus dieser Überzeugung haben wir unser Unternehmen gegründet, um mehr als nur ein Reifenhändler zu sein – wir verstehen uns als Partner auf der Reise jedes Rades.
              </p>
              <p>
                Wir sind ein junges Unternehmen mit dem Herzen eines Experten, darauf spezialisiert, Reifen neues Leben einzuhauchen. Unsere Arbeit beginnt beim Sammeln von Altreifen, worin wir nicht das Ende, sondern den Anfang einer neuen Chance sehen.
              </p>
              <p>
                Durch unsere umweltfreundlichen Verfahren stellen wir eine sichere Entsorgung und ein verantwortungsvolles Recycling sicher und verwandeln so eine ökologische Herausforderung in eine nachhaltige Lösung.
              </p>
              <p className="font-semibold text-foreground">
                Wir sind stolz darauf, ein vom Kanton Aargau offiziell bewilligter Fachbetrieb für die Reifenentsorgung zu sein.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-6">
              {stats.map((stat, index) => (
                <Card key={index} className="border-border hover:border-primary/50 transition-colors">
                  <CardContent className="p-6 text-center">
                    <stat.icon className="w-8 h-8 text-primary mx-auto mb-2" />
                    <p className="text-2xl font-bold text-foreground mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                      {stat.value}
                    </p>
                    <p className="text-sm text-muted-foreground">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          <div className="relative">
            <div className="bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl p-8 lg:p-12">
              <h3 className="text-2xl font-bold text-foreground mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Unser Versprechen
              </h3>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                    <Globe className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Weltweiter Export</h4>
                    <p className="text-sm text-muted-foreground">
                      Sorgfältig ausgewählte Gebrauchtreifen exportieren wir in neue Märkte weltweit
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-secondary flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-secondary-foreground" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Höchste Qualität</h4>
                    <p className="text-sm text-muted-foreground">
                      Hochwertige neue Reifen und Felgen, die höchsten Ansprüchen genügen
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-accent flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-accent-foreground" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Echte Partnerschaften</h4>
                    <p className="text-sm text-muted-foreground">
                      Unsere Kundenbeziehungen haben sich zu echten Partnerschaften entwickelt
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-primary-foreground" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Gemeinsam wachsen</h4>
                    <p className="text-sm text-muted-foreground">
                      Wir bedienen unsere Kunden nicht nur, wir wachsen mit ihnen
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="mt-8 p-4 bg-primary/5 rounded-lg border-l-4 border-primary">
                <p className="text-sm text-foreground italic">
                  "Pneu Idris ist nicht nur ein Name, sondern ein Versprechen für Qualität, Zuverlässigkeit und eine grünere Zukunft."
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
